import { store } from '@/App'
import HttpRequest, {
  fetchUserRegion,
  getCurrentRegion,
} from '@/config/axios.config'
import msalInstance from '@/config/msal.config'
import { isLogin, MSSSOErrorAtom } from '@/jotai/auth.jotai'
import { isEmptyValue } from '@/utils/common'
import { encrypt } from '@/utils/encrypt'
import axios from 'axios'

export async function userLogin(email: string, password: string) {
  store.set(MSSSOErrorAtom, '')
  const payload = { email, password }
  const data = await HttpRequest.post('auth/login2', {
    data: encrypt(JSON.stringify(payload)),
  }).catch((error) => error)

  if (data.data) {
    localStorage.setItem('refresh_token', data.data.data.refreshToken)
    localStorage.setItem('access_token', data.data.data.accessToken)
    const user = await HttpRequest.get('/users/profile')

    return { error: false, user: user.data.data }
  } else {
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('access_token')

    return { error: true, message: 'Login failed' }
  }
}
export async function RequestUserServer(email = '') {
  try {
    const data = await fetchUserRegion(email)
    return data
  } catch (error) {
    console.log('ERROR - Request user server ', error)
    return null
  }
}

export async function loginMicrosoftSSO() {
  store.set(MSSSOErrorAtom, '')
  const itemKey = 'msal.interaction.status'
  if (sessionStorage.getItem(itemKey)) {
    sessionStorage.removeItem(itemKey)
  }
  msalInstance.loginRedirect({
    scopes: ['User.Read'],
  })
}

export async function userLogout() {
  await HttpRequest.post('auth/logout').catch((error) => error)
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('access_token')

  ///Logout if using Microsoft SSO
  const accounts = msalInstance.getAllAccounts()
  if (!isEmptyValue(accounts)) {
    await msalInstance.logoutRedirect({
      onRedirectNavigate: () => {
        // Return false to stop navigation after local logout
        return false
      },
    })
  }

  store.set(isLogin, false)
}

export async function changeUserPassword(value) {
  const payload = {
    password: value.password,
    new_password: value.new_password,
  }
  return await HttpRequest.put('users/change-password', payload).catch(
    (error) => error.response
  )
}

function isAccessTokenValid(token: string | null): boolean {
  if (!token) return false

  try {
    const payload = token.split('.')[1]
    if (!payload) return false

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const normalizedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const decodedPayload = JSON.parse(atob(normalizedBase64))

    // Keep a small buffer to avoid requests near token expiry.
    const expiryWithBuffer = decodedPayload.exp - 30
    const nowInSeconds = Math.floor(Date.now() / 1000)

    return Number.isFinite(expiryWithBuffer) && expiryWithBuffer > nowInSeconds
  } catch {
    return false
  }
}

export async function checkAuthState(): Promise<any> {
  store.set(MSSSOErrorAtom, '')
  await msalInstance.initialize()
  const accounts = msalInstance.getAllAccounts()
  const region = getCurrentRegion()
  const { apiUrl } = JSON.parse(region)

  const refresh_token = localStorage.getItem('refresh_token')
  const access_token = localStorage.getItem('access_token')

  // Prefer a still-valid access token regardless of whether a refresh token
  // is present — this lets clients (e.g. server-side PDF rendering) bootstrap
  // with only an access token in localStorage.
  if (isAccessTokenValid(access_token)) {
    const user = await HttpRequest.get('/users/profile').catch((error) => error)
    if (user?.data?.data) {
      store.set(isLogin, true)
      return user.data.data
    }
    if (user?.response?.status && user?.response?.status !== 401) {
      throw user
    }
  }

  if (refresh_token) {
    const new_refresh_token = await HttpRequest.post('/auth/refresh').catch(
      (error) => error
    )
    if (new_refresh_token.data) {
      localStorage.setItem(
        'refresh_token',
        new_refresh_token.data.data.refreshToken
      )
      localStorage.setItem(
        'access_token',
        new_refresh_token.data.data.accessToken
      )
      const user = await HttpRequest.get('/users/profile')
      store.set(isLogin, true)
      return user.data.data
    } else {
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('access_token')
      store.set(isLogin, false)
      return null
    }
  } else if (!isEmptyValue(accounts)) {
    const request = {
      scopes: ['user.read'], // example scope
      account: accounts[0],
    }
    // TODO CHECK HERE RELATED WITH MULTI SERVER
    const ssoAxiosInstance = axios.create({
      baseURL: `${apiUrl}/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60 * 30 * 1000,
    })
    const token = await msalInstance.acquireTokenSilent(request)
    const new_refresh_token = await ssoAxiosInstance
      .post('auth/sso/entra/verify', token, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.idToken}`,
        },
      })
      .catch((error) => error)
    if (new_refresh_token.data) {
      localStorage.setItem('refresh_token', new_refresh_token.data.refreshToken)
      localStorage.setItem('access_token', new_refresh_token.data.accessToken)
      const user = await HttpRequest.get('/users/profile')
      store.set(isLogin, true)
      return user.data.data
    } else {
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('access_token')
      store.set(isLogin, false)
      store.set(MSSSOErrorAtom, new_refresh_token?.response?.data?.message)
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('access_token')
      return null
    }
  } else {
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('access_token')
    store.set(isLogin, false)
    return null
  }
}
